import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CmsRelationItem } from 'src/types/cms/cmsFieldTypes';
import { CmsPopup } from 'src/types/cms/cmsWidgetTypes';
import { RootState } from '../types/store-types';
import { useBannerActions } from './useBannerActions';
import { useGameAction } from './useGameAction';

export function useCmsPopup(popupItem?: CmsRelationItem<CmsPopup> | null): void {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { providerAction, loginAction, registrAction, promotionAction, categoryAction, externalUrlAction } =
    useBannerActions();
  const { handleActionGame } = useGameAction();

  const { tags } = useSelector((state: RootState) => state.games);
  const { categoriesData } = useSelector((state: RootState) => state.configs);
  const { selectedCategory } = useSelector((state: RootState) => state.app);

  const [tagList, setTagList] = useState<any[]>([]);
  const CMS_POPUP_OWNER_ATTR = 'data-cms-popup-owned';

  const removeOwnedElement = (element: HTMLElement | null): void => {
    if (!element) return;
    if (element.getAttribute(CMS_POPUP_OWNER_ATTR) !== 'true') {
      console.warn('Skipping removal of element not owned by useCmsPopup.');
      return;
    }
    element.remove();
  };

  useEffect(() => {
    categoriesData &&
      setTagList(categoriesData[selectedCategory]?.tags?.filter((f) => f.tagName === 'allGames')?.[0].tagGames || []);
  }, [categoriesData]);

  useEffect(() => {
    const popup = popupItem?.relation;
    if (!popup) return;
    if (!popup.htmlString) return;

    // Don't show again
    const dontShowKey = `home_page_popup_${popupItem.value}`;
    if (localStorage.getItem(dontShowKey)) return;

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.innerHTML = popup.htmlString;
    wrapper.setAttribute(CMS_POPUP_OWNER_ATTR, 'true');
    popupRef.current = wrapper;
    const overlay = wrapper.querySelector('.popup-overlay');
    const root = wrapper.querySelector('[data-popup-root="true"]');
    const closeButton = wrapper.querySelector('[data-close-button-field]');
    const dontShowBtn = wrapper.querySelector('[data-dont-show-again-button-field]');
    const buttons = Array.from(wrapper.querySelectorAll('[data-button-field]'));

    root?.setAttribute('data-disable-overlay', 'false');
    if (overlay && overlay instanceof HTMLElement) {
      overlay.style.zIndex = '99999999999';
    }

    // Button listeners
    const buttonListeners = buttons.map((button: any) => {
      const fn = (): void => {
        console.log({
          variant: button.dataset.variant,
          label: button.dataset.label,
          param: button.dataset.param,
          closeAfterAction: button.dataset.closeAfterAction,
          openInNewTab: button.dataset.openInNewTab,
        });
        switch (button.dataset.variant) {
          case 'goToRegister':
            registrAction();
            break;
          case 'goToLogin':
            loginAction();
            break;
          case 'goToPromotion':
            promotionAction(button.dataset.param);
            break;
          case 'goToProvider':
            providerAction({ provider: button.dataset.param, tags: tags, tagList: tagList });
            break;
          case 'goToExternalUrl':
            externalUrlAction(button.dataset.param);
            break;
          case 'goToGame':
            handleActionGame(button.dataset.param as number);
            break;
          case 'goToCategory':
            categoryAction(button.dataset.param);
            break;
          case 'goToRace':
          case 'noAction':
            break;
          default:
            return;
        }

        if (button.dataset.closeAfterAction) {
          closeFn();
        }
      };
      button.addEventListener('click', fn);
      return () => button.removeEventListener('click', fn);
    });

    // Close button
    const closeFn = (): void => {
      removeOwnedElement(wrapper);
      cleanup();
    };

    closeButton?.addEventListener('click', closeFn);

    // Don't show again handler
    const dontShowFn = (e: any): void => {
      const checkboxVal = e?.target?.checked;

      if (checkboxVal) {
        localStorage.setItem(dontShowKey, 'true');
      } else {
        localStorage.removeItem(dontShowKey);
      }
    };

    dontShowBtn?.addEventListener('click', dontShowFn);

    // Mount popup
    document.body.appendChild(wrapper);

    // Cleanup
    function cleanup(): void {
      closeButton?.removeEventListener('click', closeFn);
      dontShowBtn?.removeEventListener('click', dontShowFn);
      buttonListeners.forEach((remove) => remove());
    }

    return () => {
      removeOwnedElement(wrapper);
      cleanup();
    };
  }, [popupItem]);
}
